import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Flex, Text, useToast } from "@chakra-ui/react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CloudArrowUp } from "@phosphor-icons/react";
import { routes } from "../../../config/paths";
import { createSession } from "../api/dataMappingClient";
import { taskHref } from "./taskLabels";
import { useChat } from "../state/useChat";
import { useSession } from "../state/useSession";
import { useWorkingPanel } from "../state/useWorkingPanel";
import { STAGE_WORKING } from "./stages";
import { useCommitState } from "../hooks/useCommitState";
import { CommitPreview } from "./CommitPreview";
import { CommitPanel } from "./CommitPanel";

// The rail's share of the stage width — default 30%, draggable between 10% (Preview at its widest, 90%)
// and 50% (an even split). Persisted so a user's preferred split survives switching stages/sessions.
const RAIL_DEFAULT_PCT = 30;
const RAIL_MIN_PCT = 10;
const RAIL_MAX_PCT = 50;
const RAIL_WIDTH_STORAGE_KEY = "aiDataMapping.commitRailWidthPct";

const clampRailPct = (pct) => Math.max(RAIL_MIN_PCT, Math.min(RAIL_MAX_PCT, pct));

/**
 * The Commit stage's top-level layout: the brief's two columns (read-only Preview, then the Commit rail)
 * with no chat panel alongside them (that's SessionLayout's job — see its stage-aware grid). Owns the one
 * `useCommitState()` call for the whole stage, so Preview and the rail always agree on the same data.
 *
 * Rendered for BOTH the Preview and Commit tabs (WorkingPanel's MappedStage). Once an output is fully
 * committed the Commit tab disables itself (brief §4), so the plain "All data committed" view below is
 * what the user sees on Preview.
 */
export function CommitStage({ sessionId }) {
  const toast = useToast();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { questionnaireId, aiIngestTarget, hasTaskTemplate, setSession, setFile } = useSession();
  const { setMessages } = useChat();
  const { selectedOutputKey, setStage } = useWorkingPanel();
  const [committedToast, setCommittedToast] = useState(false);

  const mainRef = useRef(null);
  const [railPct, setRailPct] = useState(() => {
    const stored = parseFloat(localStorage.getItem(RAIL_WIDTH_STORAGE_KEY));
    return Number.isFinite(stored) ? clampRailPct(stored) : RAIL_DEFAULT_PCT;
  });

  useEffect(() => {
    localStorage.setItem(RAIL_WIDTH_STORAGE_KEY, String(railPct));
  }, [railPct]);

  const handleDividerPointerDown = useCallback((e) => {
    e.preventDefault();
    const main = mainRef.current;
    if (!main) return;
    const rect = main.getBoundingClientRect();

    const onMove = (ev) => {
      const pct = ((rect.right - ev.clientX) / rect.width) * 100;
      setRailPct(clampRailPct(pct));
    };
    const onUp = () => {
      document.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerup", onUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
    document.addEventListener("pointermove", onMove);
    document.addEventListener("pointerup", onUp);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  }, []);

  const commitState = useCommitState({ sessionId });

  // The toast guard is per output frame, not per CommitStage mount — switching to a different mapped
  // output (or re-mapping into a new one) must be able to toast again on its own commit.
  useEffect(() => {
    setCommittedToast(false);
  }, [selectedOutputKey]);

  const handleGoToTransform = () => setStage(STAGE_WORKING);

  const handleOpenTask = () => navigate(taskHref(routes.organisationQuestionnaire, questionnaireId));

  const handleStartNewSession = async () => {
    try {
      const s = await createSession(questionnaireId, aiIngestTarget);
      setSession(s.id, s.status, s);
      setFile(false, null);
      setMessages([]);
      const next = new URLSearchParams(searchParams);
      next.set("session", s.id);
      setSearchParams(next, { replace: true });
    } catch {
      toast({
        title: "Couldn't start a new session",
        description: "Try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Fires the "Committed to the task" toast from here (not inside useCommitState, a plain data hook) —
  // once per commit, guarded by its own flag so a later re-render (or StrictMode's double invoke) cannot
  // double-toast.
  const { committed } = commitState;
  useEffect(() => {
    if (!committed || committedToast) return;
    setCommittedToast(true);
    toast({
      title: "Committed to the task",
      description: `${(committed.rowsAccepted ?? 0).toLocaleString()} rows written${
        committed.rowsReplaced > 0 ? `, ${committed.rowsReplaced.toLocaleString()} replaced` : ""
      }.`,
      status: "success",
      duration: 6000,
      isClosable: true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [committed, committedToast]);

  if (!questionnaireId || !aiIngestTarget) {
    return (
      <CentredNotice
        title="This session isn't linked to a data task"
        body="Open AI Mapping from a section of a carbon data task to commit into it. You can still export any mapped output as a CSV from the Mapped data stage."
      />
    );
  }

  if (!selectedOutputKey) {
    return (
      <CentredNotice
        title="Nothing to commit yet"
        body="Ask the AI to map your working data onto the task's output structure. Review the result in the Mapped data stage, and it becomes available here."
        hint={
          hasTaskTemplate
            ? undefined
            : "Tip: ask the AI to read the task's output structure first — it needs that before it can map anything."
        }
      />
    );
  }

  // Brief §4: everything in this session has already been committed, and nothing has changed since.
  // Preview takes over the full width (plain mode: no legend, no segmented control, no decision rail —
  // just the "All data committed" banner over the plain table).
  //
  // Checked AFTER `committed` (the in-memory, this-render success state): a commit that just happened
  // makes `isUpToDate` true in the same instant (lastCommit now exists), and the user needs to see the
  // "N rows committed / Open task / Start a new session" rail THIS time, not the full-width banner — that
  // banner is for someone who comes back to an already-settled session later, not for the moment it settles.
  if (commitState.isUpToDate && !commitState.committed) {
    return <CommitPreview commitState={commitState} onGoToTransform={handleGoToTransform} plain />;
  }

  return (
    <Flex ref={mainRef} flex="1" minH={0} minW={0}>
      <CommitPreview commitState={commitState} onGoToTransform={handleGoToTransform} />
      <Box
        onPointerDown={handleDividerPointerDown}
        cursor="col-resize"
        bg="transparent"
        userSelect="none"
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize preview and commit rail"
        w="6px"
        flexShrink={0}
      />
      <CommitPanel
        commitState={commitState}
        onOpenTask={handleOpenTask}
        onStartNewSession={handleStartNewSession}
        width={`${railPct}%`}
      />
    </Flex>
  );
}

const CentredNotice = ({ title, body, hint }) => (
  <Flex flex="1" align="center" justify="center" direction="column" gap={3} py={20} color="gray.500">
    <Box w="56px" h="56px" borderRadius="xl" bg="lightLime" display="flex" alignItems="center" justifyContent="center">
      <CloudArrowUp size={26} color="#85C22A" weight="fill" />
    </Box>
    <Text fontSize="20px" fontWeight="700" color="darkTeal">
      {title}
    </Text>
    <Text fontSize="13px" color="gray.500" textAlign="center" maxW="46ch">
      {body}
    </Text>
    {hint && (
      <Text fontSize="12px" color="gray.400" textAlign="center" maxW="46ch">
        {hint}
      </Text>
    )}
  </Flex>
);

export default CommitStage;
